import { Box, Typography } from "@mui/material"
import { useNavigate, useSearch } from "@tanstack/react-router"
import type { FC } from "react"

import { ACCOUNT_CLASSIFICATION_LABELS, ACCOUNT_TYPE_LABELS, REPORTING_ACCOUNT_TYPE_LABELS } from "../../constants"
import { RemoveFilterType } from "../../enums"

import { FilterChip } from "@/components/common/Filters"
import type { GlAccountType } from "@/graphql/codegen/graphql"

export const FilterChips: FC = () => {
  const navigate = useNavigate({ from: "/app/admin/accounting/accounts-chart" })
  const currentSearch = useSearch({
    from: "/app/admin/accounting/_layout/accounts-chart",
    strict: true,
  })

  const { classification, accountTypes = [], reportingAccountType } = currentSearch

  const handleRemoveFilter = async (type: RemoveFilterType, value: unknown) => {
    const updatedSearch = { ...currentSearch }

    switch (type) {
      case RemoveFilterType.Classification:
        updatedSearch.classification = undefined
        break
      case RemoveFilterType.AccountTypes:
        updatedSearch.accountTypes = (currentSearch.accountTypes ?? []).filter((accountType) => accountType !== value)
        break
      case RemoveFilterType.ReportingAccountType:
        updatedSearch.reportingAccountType = undefined
        break
      default:
        break
    }

    await navigate({
      search: updatedSearch,
      replace: true,
    })
  }

  if (!classification && !accountTypes.length && !reportingAccountType) {
    return null
  }

  return (
    <Box data-testid='chart-of-accounts-filter-chips' display='flex' flexWrap='wrap' alignItems='center' gap={2}>
      <Typography variant='body1'>Filters:</Typography>

      {classification && (
        <FilterChip
          key={`filter-chip-${classification}`}
          testId={`filter-chip-${classification}`}
          label={`Classification: ${ACCOUNT_CLASSIFICATION_LABELS[classification]}`}
          onDelete={() => handleRemoveFilter(RemoveFilterType.Classification, classification)}
        />
      )}

      {accountTypes.map((accountType: GlAccountType) => (
        <FilterChip
          key={`filter-chip-${accountType}`}
          testId={`filter-chip-${accountType}`}
          label={`Account Type: ${ACCOUNT_TYPE_LABELS[accountType]}`}
          onDelete={() => handleRemoveFilter(RemoveFilterType.AccountTypes, accountType)}
        />
      ))}

      {reportingAccountType && (
        <FilterChip
          key='filter-chip-reporting-account-type'
          testId='filter-chip-reporting-account-type'
          label={`Reporting Account Type: ${REPORTING_ACCOUNT_TYPE_LABELS[reportingAccountType]}`}
          onDelete={() => handleRemoveFilter(RemoveFilterType.ReportingAccountType, null)}
        />
      )}
    </Box>
  )
}
